import { get, size } from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  all,
  call,
  cancelled,
  delay,
  fork,
  put,
  select,
  takeLatest
} from "redux-saga/effects";
import { openToast } from "~/components/common/molecules/ToastNotification";
import history from "~/history";
import { GET_MEDICAL_BILL_DETAIL_SUCCESS } from "~/store/booking/bookingType";
import * as notiActions from "~/store/notifications/notificationsActions";
import { apiRequestHealthInsuranceCard } from "~/store/payment/paymentApi";
import * as types from "~/store/payment/paymentType";
import * as totalDataActions from "~/store/totalData/actions";
import { huyi } from "~/utils/clog";
import { CSGroupID, REDIRECT_URL_AFTER_BOOKING } from "~/utils/constants";
import { getDataToReserve, getRouteFollowPartnerId } from "~/utils/func";
import { getBaseUrl } from "~/utils/getBaseUrl";
import { client } from "~/utils/medproSDK";
import AppId from "~/utils/partner";
import * as actions from "~/store/booking/bookingAction";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const VIETNAM_TZ = "Asia/Ho_Chi_Minh";

const getCombinedBookingDate = (date, timeslot) => {
  if (!date) return "";
  const datePart = dayjs(date)
    .tz(VIETNAM_TZ)
    .format("YYYY-MM-DD");
  const timePart = timeslot?.startTime || "00:00";
  const combinedStr = `${datePart} ${timePart}`;
  return dayjs.tz(combinedStr, "YYYY-MM-DD HH:mm", VIETNAM_TZ).toISOString();
};

function* requestHealthInsuranceCard() {
  try {
    const baseUrl = yield select(state => getBaseUrl());
    const { info } = yield select(state => state.user);
    const { paymentID } = yield select(state => state.healthInsuranceCard);
    const response = yield call(
      apiRequestHealthInsuranceCard,
      baseUrl,
      info,
      paymentID
    );
    if (response.error_code) throw new Error("Server error");
    const { data } = response;
    yield put({
      type: types.REQUEST_HEALTH_INSURANCE_CARD_SUCCESS,
      data
    });
    yield put({ type: GET_MEDICAL_BILL_DETAIL_SUCCESS, data: data[0] });
    yield put(
      notiActions.getNotification({
        user_id: info.user_id,
        access_token: info.access_token
      })
    );
  } catch (error) {
    yield put({
      type: types.REQUEST_HEALTH_INSURANCE_CARD_FAIL,
      error
    });
  }
}

function* watchRequestHealthInsuranceCard() {
  yield takeLatest(
    types.REQUEST_HEALTH_INSURANCE_CARD,
    requestHealthInsuranceCard
  );
}

// GET ALL PAYMENTS
function* getAllPaymentMethod({ payload }) {
  try {
    const totalData = yield select(state => state.totalData);
    const patient = yield select(state => state.patient);

    const schedulesSelected = get(totalData, "schedulesSelected", null);
    const isRepayment = yield select(state => state.totalData.isRepayment);
    const days = yield select(state => state.totalData.days);
    const timeSlot = yield select(state => state.totalData.timeSlot);
    const groupId = isRepayment ? 4 : 3;
    const global = yield select(state => state.global);
    const platformType = get(global, "device.type");
    const platformDefault = platformType === "desktop" ? "pc" : "web";
    const platform = payload.platform || platformDefault;
    const { cskhToken } = yield select(state => state.customerService);
    const urlParams = new URLSearchParams(window.location.search);
    const isUser = urlParams.get("isUser");
    const addonMedproCare = yield select(
      state => state.booking.addonMedproCare
    );
    const bookingDate = getCombinedBookingDate(days?.date, timeSlot);
    const currentDate = dayjs
      .utc()
      .tz(VIETNAM_TZ, true)
      .toISOString();

    const arrBooking = schedulesSelected.map(schedule => {
      const price = get(schedule, "service.price", 0);
      const bookingDateTransfer =
        totalData?.service?.pickType === "LATER" ? currentDate : bookingDate;
      return {
        bookingId: schedule?.id || "",
        patientId: patient?.selectedPatientId || "",
        price: price,
        groupId,
        treeId: totalData.treeId || "",
        serviceId: schedule?.service?.id || "",
        subjectId: schedule?.subject?.id || "",
        roomId: schedule?.room?.id || "",
        doctorId: schedule?.doctor?.id || "",
        bookingDate: bookingDateTransfer,
        addonServices: totalData.addonServices?.map(i => i.id) || [],
        medproCareServiceIds:
          size(addonMedproCare) > 0 ? [addonMedproCare?.id] : []
      };
    });
    const response = yield client.getAllPayment(
      {
        ...arrBooking[0]
      },
      {
        partnerid: totalData.partnerId || AppId,
        appid: AppId,
        platform,
        cskhtoken: isUser ? "" : cskhToken
      }
    );
    const { data } = response;

    yield put({
      type: types.GET_ALL_PAYMENT_METHODS_SUCCESS,
      data
    });

    // cskh: groupId = 3, customer = 4, dựa vào biến repayment
  } catch (error) {
    yield put({
      type: types.GET_ALL_PAYMENT_METHODS_FAIL,
      error
    });
  }
}

function* watchRequestAllPaymentMethod() {
  yield takeLatest(types.GET_ALL_PAYMENT_METHODS, getAllPaymentMethod);
}

// ALL PAYMENT FEE METHODS
function* requestGetAllFeePaymentMethods() {
  try {
    const global = yield select(state => state.global);
    const platformType = get(global, "device.type");
    const platform = platformType === "desktop" ? "pc" : "web";
    const fee = yield select(state => state.fee);
    const priceFeePayment = get(fee, "selectedFee.amount", 0);

    const partnerid = yield select(state => state.totalData.partnerId);
    const response = yield client.getAllPaymentMethod(
      { price: priceFeePayment, groupId: 2 },
      { partnerid: partnerid, appid: AppId, platform }
    );
    const { data } = response;
    yield put({
      type: types.GET_ALL_PAYMENT_METHODS_SUCCESS,
      data
    });
  } catch (error) {
    yield put({
      type: types.GET_ALL_PAYMENT_METHODS_FAIL,
      error
    });
  }
}

function* watchRequestAllFeePaymentMethod() {
  yield takeLatest(
    types.GET_ALL_PAYMENT_METHOD_FEE,
    requestGetAllFeePaymentMethods
  );
}

// ALL CSKH PAYMENT METHODS
export function* requestGetAllCSKHPaymentMethods() {
  try {
    const totalData = yield select(state => state.totalData);
    const bookingInfos = get(totalData, "schedulesSelected", []);
    const booking = bookingInfos.length > 0 ? bookingInfos[0] : null;
    const price = get(booking, "service.price", 0);

    const partnerid =
      totalData.paymentInformation[0]?.bookingInfo?.partnerId ||
      (yield select(state => state.totalData.partnerId));
    const treeId = get(totalData, "treeId", "");
    const obj = {
      price: price || 123,
      groupId: CSGroupID,
      treeId,
      serviceId: booking.service.id,
      subjectId: booking.subject.id,
      roomId: booking.room.id,
      doctorId: booking.doctor.id,
      bookingDate: dayjs
        .utc(booking?.date)
        .tz(VIETNAM_TZ, true)
        .toISOString()
    };
    const global = yield select(state => state.global);
    const platformType = get(global, "device.type");
    const platform = platformType === "desktop" ? "pc" : "web";

    const response = yield client.getAllPaymentMethod(obj, {
      partnerid: partnerid,
      appid: AppId,
      platform
    });
    const { data } = response;
    yield put({
      type: types.GET_ALL_PAYMENT_METHODS_SUCCESS,
      data
    });
  } catch (error) {
    yield put({
      type: types.GET_ALL_PAYMENT_METHODS_FAIL,
      error
    });
  }
}

function* watchRequestAllCSKHPaymentMethod() {
  yield takeLatest(
    types.GET_ALL_PAYMENT_METHOD_CSKH,
    requestGetAllFeePaymentMethods
  );
}

// CHECK REQUIRE PAYMENT
function* requestCheckRequirePayment() {
  try {
    yield put(actions.loadingUpdateBooking());
    const totalDataReducer = yield select(state => state.totalData);
    const globalReducer = yield select(state => state.global);
    const paymentMethodReducer = yield select(state => state.payment);
    const patientReducer = yield select(state => state.patient);
    const reExamReducer = yield select(state => state.followUpExam);
    const bookingReducer = yield select(state => state.booking);

    const partnerId = get(totalDataReducer, "partnerId");
    const cskhtoken = yield select(
      state => state.customerService.selectedPatient.secretKey
    );

    const postData = getDataToReserve(
      totalDataReducer,
      globalReducer,
      paymentMethodReducer,
      patientReducer,
      reExamReducer,
      bookingReducer
    );

    const price = get(totalDataReducer, "service.price", "0");

    postData.groupId = CSGroupID;
    postData.subTotal = price;
    postData.redirectUrl = REDIRECT_URL_AFTER_BOOKING;

    const global = yield select(state => state.global);
    const platformType = get(global, "device.type");
    const platform = platformType === "desktop" ? "pc" : "web";

    const response = yield client.isRequirePayment(postData, {
      partnerid: partnerId,
      appid: AppId,
      cskhtoken,
      platform
    });
    const isRequirePayment = get(response, "data.isRequirePayment", true);
    if (isRequirePayment) {
      const route = getRouteFollowPartnerId("/hinh-thuc-thanh-toan", partnerId);
      history.push(route);
      yield put(actions.updateBookingSuccess());
    } else {
      yield put(totalDataActions.reserveBooking());
    }
  } catch (error) {
    yield put(actions.updateBookingFail());
    console.log(error);
  }
}

function* watcherRequestCheckRequirePayment() {
  yield takeLatest(types.CHECK_REQUIRE_PAYMENT, requestCheckRequirePayment);
}

function* checkStatus() {
  try {
    const global = yield select(state => state.global);
    const platformType = get(global, "device.type");
    const platform = platformType === "desktop" ? "pc" : "web";

    const totalReducer = yield select(state => state.totalData);
    const transactionId = get(
      totalReducer,
      "paymentInformation.bookingInfo.transactionId"
    );

    if (transactionId) {
      const response = yield client.checkTransaction(
        {
          transactionId
        },
        { platform }
      );

      const { status } = response.data;

      switch (status) {
        case 1:
          console.log("tao don hang");
          break;
        case 2:
          console.log("thanh cong");
          openToast("Giao dịch thành công.");
          yield put({
            type: types.STOP_GET_STATUS_TRANSACTION
          });
          history.push("/");
          break;
        case 3:
          console.log("that bai");
          yield put({
            type: types.STOP_GET_STATUS_TRANSACTION
          });

          openToast("Giao dịch thất bại.", "error");
          history.push("/");

          break;
        default:
          yield put({
            type: types.STOP_GET_STATUS_TRANSACTION
          });
          openToast("default", "error");
          break;
      }
    }
  } catch (error) {
    yield put({
      type: types.STOP_GET_STATUS_TRANSACTION
    });
    openToast("Giao dịch error", "error");
  }
}

function* requestStatusTransaction({ status }) {
  if (status === "active") {
    yield put({
      type: types.ACTIVE_STATUS_TRANSACTION
    });
  }

  let loopStatus = yield select(state => state.payment.loopStatus);

  try {
    while (loopStatus) {
      yield call(checkStatus);
      yield delay(5000);
      loopStatus = yield select(state => state.payment.loopStatus);
    }
  } finally {
    if (yield cancelled()) {
      checkStatus.close();
      requestStatusTransaction.close();
    }
  }
}

function* watchRequestStatusTransaction() {
  yield takeLatest(types.GET_STATUS_TRANSACTION, requestStatusTransaction);
}

// GET ALL PAYMENTS multiple
function* getAllPaymentMultiBooking({ payload }) {
  try {
    const totalData = yield select(state => state.totalData);
    const patient = yield select(state => state.patient);

    const priceOfAddOn =
      totalData.addonServices?.reduce((init, { price }) => {
        return init + price;
      }, 0) || 0;

    const bookingInfos = get(totalData, "schedulesSelected", []);
    const partnerid = yield totalData.paymentInformation?.[0]?.bookingInfo
      ?.partnerId || select(state => state.totalData.partnerId);

    const treeId = get(totalData, "treeId", "");
    const { cskhToken } = yield select(state => state.customerService);
    const globalReducer = yield select(state => state.global);
    const platformType = get(globalReducer, "device.type");

    const platform = platformType === "desktop" ? "pc" : "web";

    const arrBooking = bookingInfos.map(booking => {
      const price = get(booking, "service.price", 0);
      const groupId = totalData.cskhUserId ? 4 : 1;
      return {
        bookingId: booking?.id,
        patientId: patient?.selectedPatientId,
        price: (payload.price || price) + priceOfAddOn,
        groupId,
        treeId,
        serviceId: booking.service?.id,
        subjectId: booking.subject?.id,
        roomId: booking.room?.id,
        doctorId: booking.doctor?.id,
        bookingDate: dayjs
          .utc(booking?.date)
          .tz(VIETNAM_TZ, true)
          .toISOString(),
        addonServices: totalData.addonServices?.map(i => i.id) || []
      };
    });

    const urlParams = new URLSearchParams(window.location.search);

    const isUser = urlParams.get("isUser");

    const response = yield client.getAllPaymentMultiBooking(
      {
        bookings: arrBooking
      },
      {
        partnerid: partnerid || AppId,
        appid: AppId,
        platform,
        treeId,
        cskhtoken: isUser ? "" : cskhToken
      }
    );
    const { data } = response;

    yield put({
      type: types.GET_ALL_PAYMEN_MULTI_BOOKING_METHODS_SUCCESS,
      data
    });

    if (AppId === "momo") {
      // tìm hình thức là momo
      const momoMethod = data.find(item => item.methodId === "momo");

      // chọn hình thức thanh toán là momo
      yield put({
        type: types.SELECTED_PAYMENT_METHOD,
        method: momoMethod.paymentTypes[0],
        methodId: momoMethod.methodId
      });
    }
  } catch (error) {
    huyi({
      name: "client.getAllPaymentMultiBooking",
      child: error,
      type: "error"
    });
  }
}

function* watchRequestAllPaymentMethodMuitple() {
  yield takeLatest(
    types.GET_ALL_PAYMEN_MULTI_BOOKING_METHODS,
    getAllPaymentMultiBooking
  );
}

export default function* root() {
  yield all([
    fork(watchRequestStatusTransaction),
    fork(watchRequestHealthInsuranceCard),
    fork(watchRequestAllPaymentMethod),
    fork(watchRequestAllFeePaymentMethod),
    fork(watchRequestAllCSKHPaymentMethod),
    fork(watcherRequestCheckRequirePayment),
    fork(watchRequestAllPaymentMethodMuitple)
  ]);
}
